/**
 * Where one SQL statement ends and the next begins — for "run the statement at
 * the cursor" (Ctrl+Enter in a DB Manager query tab).
 *
 * Owner rule, 30.09.2026: a statement ends at `;` OR at an empty line (a line
 * holding only whitespace). Both only count outside string literals, quoted
 * identifiers, comments and Postgres dollar-quoted bodies, so a `;` or an empty
 * line inside `'…'`, `/* … *\/` or `$$ … $$` never splits anything.
 *
 * A selection is NOT split here — the editor runs a selection exactly as the
 * user marked it (see SqlQueryEditor `getRunnableSql`).
 *
 * `#` starts a line comment only on MySQL/MariaDB; on Postgres it is an
 * operator (`a # b`) and on SQLite it is not a comment either.
 */

const isBlank = (ch) => ch === ' ' || ch === '\t' || ch === '\r';

/** `$tag$` opening at `i` (Postgres), or null. */
function dollarTagAt(text, i) {
  const m = /^\$([A-Za-z_][A-Za-z0-9_]*)?\$/.exec(text.slice(i, i + 64));
  return m ? m[0] : null;
}

/**
 * Every statement in `text` as `{ start, end }` offsets of its trimmed text
 * (end exclusive, a trailing `;` included). Segments holding only comments or
 * whitespace are dropped — they are not something a database can run.
 */
export function scanSqlStatements(text, { engine } = {}) {
  const src = String(text ?? '');
  const hashComments = engine === 'mysql';
  const dollarQuotes = engine === 'postgres';
  const out = [];
  let firstCode = -1; // first offset of real SQL in the segment
  let lastCode = -1; // last offset of real SQL in the segment

  const close = () => {
    if (firstCode !== -1) out.push({ start: firstCode, end: Math.max(lastCode + 1, firstCode) });
    firstCode = -1;
    lastCode = -1;
  };
  const code = (from, to) => {
    if (firstCode === -1) firstCode = from;
    lastCode = to;
  };

  let i = 0;
  while (i < src.length) {
    const ch = src[i];
    const next = src[i + 1];

    // Line comment: skip to the newline (the newline itself is handled below,
    // so a comment line followed by an empty line still splits).
    if ((ch === '-' && next === '-') || (hashComments && ch === '#')) {
      while (i < src.length && src[i] !== '\n') i += 1;
      continue;
    }
    // Block comment.
    if (ch === '/' && next === '*') {
      const endAt = src.indexOf('*/', i + 2);
      i = endAt === -1 ? src.length : endAt + 2;
      continue;
    }
    // String literal / quoted identifier.
    if (ch === "'" || ch === '"' || ch === '`') {
      const from = i;
      i += 1;
      while (i < src.length) {
        if (src[i] === '\\' && ch !== '`') {
          i += 2;
          continue;
        }
        if (src[i] === ch) {
          // '' and "" are escaped quotes inside the literal.
          if (src[i + 1] === ch) {
            i += 2;
            continue;
          }
          break;
        }
        i += 1;
      }
      code(from, Math.min(i, src.length - 1));
      i += 1;
      continue;
    }
    // Postgres dollar-quoted body ($$ … $$ or $fn$ … $fn$).
    if (dollarQuotes && ch === '$') {
      const tag = dollarTagAt(src, i);
      if (tag) {
        const endAt = src.indexOf(tag, i + tag.length);
        const stop = endAt === -1 ? src.length : endAt + tag.length;
        code(i, stop - 1);
        i = stop;
        continue;
      }
    }
    if (ch === ';') {
      code(i, i);
      close();
      i += 1;
      continue;
    }
    if (ch === '\n') {
      // An empty line = this newline, then only blanks, then another newline.
      let j = i + 1;
      while (j < src.length && isBlank(src[j])) j += 1;
      if (src[j] === '\n') {
        close();
        i = j; // the second newline is examined next (a run of empty lines is one boundary)
        continue;
      }
      i += 1;
      continue;
    }
    if (!isBlank(ch)) code(i, i);
    i += 1;
  }
  close();
  return out;
}

/**
 * The whole script as the database should receive it (Ctrl+Shift+Enter):
 * every statement by the same rules, each closed with `;`. The server only
 * splits on `;`, so statements separated by an empty line alone would
 * otherwise reach it as ONE statement and fail. Comment-only parts are dropped.
 */
export function scriptForRun(text, { engine } = {}) {
  const src = String(text ?? '');
  return scanSqlStatements(src, { engine })
    .map(({ start, end }) => {
      const sql = src.slice(start, end);
      return sql.endsWith(';') ? sql : `${sql};`;
    })
    .join('\n');
}

/**
 * The statement to run for a cursor at `offset`, as `{ start, end, sql }`, or
 * null when there is nothing to run there (an empty line, a comment, an empty
 * document). Never falls back to the whole script — that is a separate,
 * deliberate action.
 *
 *  - Cursor inside a statement, or right after its last character → that one.
 *  - Cursor later on the same line as a statement's end (e.g. after `;` and
 *    some spaces) → the statement that ended last on that line.
 */
export function statementRangeAtOffset(text, offset, { engine } = {}) {
  const src = String(text ?? '');
  const at = Math.max(0, Math.min(Number(offset) || 0, src.length));
  const segments = scanSqlStatements(src, { engine });
  const hit = segments.find((s) => at >= s.start && at <= s.end);
  const pick =
    hit ||
    segments
      .filter((s) => s.end <= at && !src.slice(s.end, at).includes('\n'))
      .pop();
  return pick ? { start: pick.start, end: pick.end, sql: src.slice(pick.start, pick.end) } : null;
}
